<template>
  <div>
    <div class="container mx-auto">
      <div class="row mx-auto">
        <h3 class="mb-0">Data Downloads</h3>
      </div>
      <p>
        As per our Data Use Policy, all research publications using data from
        projects hosted on the CitSci platform (those listed on our Projects
        page) are required to cite the use of the platform. Citations provide
        CitSci credit as an integral component of methods and data collection.
        Recognition through citations is one metric funders use to evaluate our
        value and ensure that we can continue to provide data collection and
        support services to partners like you for free.
        View
        <b-link :to="{ name: 'Publications' }">additional information</b-link>
        .
      </p>
      <p>
        If you do publish,
        <ContactUsIconLink hyperlink-text="let us know"></ContactUsIconLink>
        and we'll add it to our
        <b-link :to="{ name: 'Publications' }">Publications</b-link>
        page.
      </p>
      <div class="row mx-auto justify-content-center">
        <h5>
          <i class="fa-solid fa-circle-info fa-lg"></i>
          <b>Note:</b> <i>{{ getExpiryMessage() }}</i>
        </h5>
      </div>
    </div>
    <b-table
      show-empty
      stacked="md"
      id="userDownloadsTable"
      ref="userDownloadsTable"
      :items="userDownloads"
      :fields="visibleFields"
      :current-page="currentPage"
      :per-page="perPage"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :sort-direction="sortDirection"
      :empty-text="emptyText"
      :sort-icon-left="true"
    >
      <template v-slot:cell(createdAt)="row">
        <DateTime :date="row.item.createdAt" :dateTime="false"></DateTime>
        <div class="small text-muted">{{ getExpiryDateString(row.item) }}</div>
      </template>
      <template v-slot:cell(citation)="row">
        <div v-if="row.item.status === 'completed' || row.item.status === 'expired'">
          <div>
            {{ row.item.project.name }}. ({{
              moment(row.item.createdAt).format("MMMM YYYY")
            }}). [Data Set]. <i>CitSci</i>
            <a :href=getProjectCitation(row.item.project)>
              {{ getProjectCitation(row.item.project) }}
            </a>
            Accessed: {{ getCitationAccessedDate(row.item) }}.
          </div>
        </div>
      </template>
      <template v-slot:cell(options)="row">
        <div class="options-holder right">
          <div class="btn btn-secondary btn-sm"
               v-if="row.item.status === 'completed' && row.item.fileObject !== null"
               @click="onClickViewDownload(row.item.fileObject)"
               title="Download File"
          >
            <i class="fa fa-download mr-1"></i>Download
          </div>
        </div>
      </template>
      <template #table-busy>
        <VueLoader style="height: 200px" text="Loading Your Downloads..."/>
      </template>
    </b-table>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import VueLoader from "../../../layout/Loader";
import DateTime from "../../../common/date-time/DateTime.vue";
import moment from "moment";
import IconLink from "../../../layout/IconLink.vue";
import ContactUsIconLink from "../../../layout/ContactUsIconLink.vue";

export default {
  name: "ProjectDownloads",
  components: {
    ContactUsIconLink,
    IconLink,
    VueLoader,
    DateTime,
  },
  props: {},
  data() {
    return {
      isBusy: false,
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      sortBy: "createdAt",
      sortDesc: true,
      sortDirection: "desc",
      emptyText: "No Downloads Yet!",

      fieldList: [
        {
          key: "createdAt",
          label: "Created Date",
          sortable: true,
          class: "text-left",
          visible: true,
        },
        {
          key: "status",
          label: "Status",
          sortable: false,
          class: "text-center",
          visible: true,
          formatter: (val) => {
            return val.charAt(0).toUpperCase() + val.slice(1);
          },
        },
        {
          key: "citation",
          label: "Citation",
          sortable: false,
          class: "text-left",
          visible: true,
        },
        {
          key: "options",
          label: "Options",
          class: "text-right",
          visible: true,
        },
      ],
    };
  },

  computed: {
    ...mapGetters({
      userId: "user/profile/id",
      projectId: "project/get/id",
    }),
    visibleFields() {
      // Property returns only the fields where visible = true
      return this.fieldList.filter((field) => field.visible);
    },
  },

  methods: {
    ...mapActions({
      getUserDownloads: "user/project_downloads/get",
    }),

    moment(value) {
      return moment(value);
    },

    getExpiryMessage() {
      let start = new Date('2026-11-01')
      if (new Date() < start) {
        return 'Starting November 1st, 2026 data downloads older than 30 days will be automatically removed. Your request history is kept, and you can generate a new download at any time.'
      }
      return 'Data downloads older than 30 days are automatically removed. Your request history is kept, and you can generate a new download at any time.'
    },

    getExpiryDateString(download) {
      let status = download.status
      if (status === 'completed') {
        let expires = this.moment(download.createdAt).add(30, 'days')
        let start = this.moment('2026-11-01')
        // Removal does not begin until the start date, so anything due before then lasts at least that long.
        if (expires.isBefore(start)) {
          return "Expires: " + start.format('MMM DD, YYYY')
        }
        return "Expires: " + expires.format('MMM DD, YYYY')
      }
      else if (status === 'expired') {
        return "Expired: " + this.moment(download.updatedAt).format('MMM DD, YYYY')
      }
      else {
        return null
      }
    },

    getCitationAccessedDate(download) {
      // An expired file can no longer be accessed, so the citation uses the last full day it was available.
      let accessed = download.status === 'expired'
        ? this.moment(download.updatedAt).subtract(1, 'days')
        : this.moment(Date.now())
      return accessed.format('MMMM DD, YYYY')
    },

    getProjectCitation(project) {
      return this.$appRootUrl + '/projects/' + project.urlField
    },

    userDownloads(ctx) {
      this.isBusy = true;
      let urlParams = `${this.urlBuilder(ctx)}`;

      return this.getUserDownloads({urlParams}).then((data) => {
        this.totalItems = data["hydra:totalItems"];
        this.currentPage = ctx.currentPage || 1;
        this.sortBy = ctx.sortBy || "";
        this.isBusy = false;

        return data["hydra:member"].map((downloads) => {
          return {
            ...downloads,
          };
        })
        // Uncomment to color rows based on status column
        // .map(item => {
        //   item._rowVariant = this.getRowColor(item.status)
        //   return item
        // })
      });
    },

    onClickViewDownload(data) {
      if (data.path !== null || data.path !== "") {
        window.open(data.path, "_blank");
      }
    },

    getRowColor(status) {
      // Returns row color
      switch (status) {
        case "completed":
          return "success";
        case "submitted":
        case "in progress":
          return "active";
        case "failed":
        case "expired":
          return "danger";
      }
    },
  },
};
</script>

<style></style>
